import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button, ListGroup, Form, Alert } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createSLMTitleDetailsListElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

const DATA_SET = "titleDetails";
const ENDPOINT = "slmTitleDetails";
const PRIMARY_KEY = "parcel_id";

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

export default function SLMTitleDetailsList({
  filter,
  data = [],
  dataFetch,
  isLoading = false,
  isAuthenticated,
  controlType = "list",
  saveRecord,
  updateTableState,
  context,
  ctx,
  stateCode,
  listAmount,
  user,
  validateRelatedRecord,
}) {
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const rowsRef = useRef(rows);
  const tableHandlersRef = useRef({
    updateCell: null,
    commitCell: null,
    deleteRow: null,
    copyRecord: null,
  });
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const createId = useMemo(() => createElementIdFactory("slm-title-details-list"), []);
  const elementIds = useMemo(() => createSLMTitleDetailsListElementIds(createId), [createId]);
  const columnHelper = useMemo(() => createColumnHelper(), []);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const foreignKey = useMemo(() => {
    const activeContext = (context || ctx || "").toLowerCase();
    if (activeContext === "slmlease") {
      return { key: "lease_id", value: filter?.leaseid ?? null };
    }
    if (activeContext === "slmsale") {
      return { key: "sale_id", value: filter?.saleid ?? null };
    }

    if (filter?.leaseid || filter?.lease_id) {
      return { key: "lease_id", value: filter?.leaseid ?? filter?.lease_id ?? null };
    }

    if (filter?.saleid || filter?.sale_id) {
      return { key: "sale_id", value: filter?.saleid ?? filter?.sale_id ?? null };
    }

    const pathname = window?.location?.pathname?.toLowerCase() || "";
    if (pathname.includes("/slmleases/")) {
      return { key: "lease_id", value: null };
    }

    if (pathname.includes("/slmsales/")) {
      return { key: "sale_id", value: null };
    }

    return { key: "property_id", value: filter?.propertyid ?? null };
  }, [context, ctx, filter]);

  const syncRows = useCallback((nextRows) => {
    updateTableState?.(nextRows, DATA_SET);
  }, [updateTableState]);

  const showValidationError = useCallback((message) => {
    setErrorMessage(message || "Validation failed.");
    setShowAlert(true);
  }, []);

  const clearValidationError = useCallback(() => {
    setShowAlert(false);
    setErrorMessage("");
  }, []);

  const normalizeValidationResult = useCallback((result, fallbackMessage) => {
    if (Array.isArray(result)) {
      const [isValid = true, validationMessage = ""] = result;
      return { isValid: Boolean(isValid), message: validationMessage || fallbackMessage || "Invalid record." };
    }

    if (result && typeof result === "object") {
      const isValid = result.isValid !== undefined ? Boolean(result.isValid) : true;
      const message = result.validationMessage || result.message || fallbackMessage || "Invalid record.";
      return { isValid, message };
    }

    if (typeof result === "boolean") {
      return { isValid: result, message: fallbackMessage || "Invalid record." };
    }

    return { isValid: true, message: "" };
  }, []);

  const runRelatedValidation = useCallback((record) => {
    if (typeof validateRelatedRecord !== "function") {
      return { isValid: true, message: "" };
    }

    return normalizeValidationResult(validateRelatedRecord(record, ENDPOINT), "Parcel validation failed.");
  }, [normalizeValidationResult, validateRelatedRecord]);

  const isTitleDetailsValid_ACT = (titleDetails) => {
    if (!/^[A-Z]{4}$/.test(titleDetails.district || "")) {
      return [false, "District not 4 uppercase letters"];
    }

    if (!/^[A-Z]{4}$/.test(titleDetails.division || "")) {
      return [false, "Division not 4 uppercase letters"];
    }

    if (!/^\d{1,3}$/.test(titleDetails.section || "")) {
      return [false, "Section not 1-3 digits"];
    }

    if (!/^\d{1,4}$/.test(titleDetails.block || "")) {
      return [false, "Block not 1-4 digits"];
    }

    if (titleDetails.unit && !/^\d{1,3}$/.test(titleDetails.unit)) {
      return [false, "Unit not 1-3 digits"];
    }

    return [true, null];
  };

  const isTitleDetailsValid_NSW = (titleDetails) => {
    if (titleDetails.lot_number && !/^[A-Z\d]+$/.test(titleDetails.lot_number)) {
      return [false, "Lot Number not alphanumeric"];
    }

    if (titleDetails.section && !/^[A-Z\d]+$/.test(titleDetails.section)) {
      return [false, "Section not alphanumeric"];
    }

    if (titleDetails.plan_prefix && !/^[A-Z]{1,2}$/.test(titleDetails.plan_prefix)) {
      return [false, "Plan Prefix not 1-2 uppercase letters"];
    }

    if (!/^\d+$/.test(titleDetails.plan_number || "")) {
      return [false, "Plan number not numeric"];
    }

    return [true, null];
  };

  const isTitleDetailsValid_NT = (titleDetails) => {
    if (!/^\d{3}$/.test(titleDetails.location_code || "")) {
      return [false, "Location Code not 3 digits"];
    }

    if (titleDetails.lot_number && !/^[A-Z\d]+$/.test(titleDetails.lot_number)) {
      return [false, "Lot Number not alphanumeric"];
    }

    if (titleDetails.survey_plan && !/^[A-Z\d]+ *[A-Z/\d]+$/.test(titleDetails.survey_plan)) {
      return [false, "Survey Plan not alphanumeric with (optional) forward-slashes"];
    }

    if (titleDetails.unit && !/^\d+$/.test(titleDetails.unit)) {
      return [false, "Unit not numeric"];
    }

    return [true, null];
  };

  const isTitleDetailsValid_QLD = (titleDetails) => {
    if (titleDetails.lot_number && !/^[A-Z\d]+$/.test(titleDetails.lot_number)) {
      return [false, "Lot Number not alphanumeric"];
    }

    if (!/^[A-Z]{1,3}$/.test(titleDetails.plan_prefix || "")) {
      return [false, "Plan Prefix not 1-3 uppercase letters"];
    }

    if (!/^\d+$/.test(titleDetails.plan_number || "")) {
      return [false, "Plan Number not numeric"];
    }

    return [true, null];
  };

  const isTitleDetailsValid_SA = (titleDetails) => {
    if (titleDetails.lot_number && !/^[A-Z\d]+$/.test(titleDetails.lot_number)) {
      return [false, "Lot Number not alphanumeric"];
    }

    if (!titleDetails.plan_prefix) {
      return [false, "Plan Prefix must be set"];
    }

    if (!titleDetails.plan_number) {
      return [false, "Plan Number must be set"];
    }

    return [true, null];
  };

  const isTitleDetailsValid_TAS = (titleDetails) => {
    if (titleDetails.lot_number && !/^\d+$/.test(titleDetails.lot_number)) {
      return [false, "Lot Number not numeric"];
    }

    if (!/^\d+$/.test(titleDetails.plan_number || "")) {
      return [false, "Plan Number not numeric"];
    }

    return [true, null];
  };

  const isTitleDetailsValid_VIC = (titleDetails) => {
    const atLeastOneOtherAttributeSet = titleDetails.crown_allotment_number || titleDetails.section || titleDetails.portion || titleDetails.block;

    if ((titleDetails.parish_code && !atLeastOneOtherAttributeSet) || (!titleDetails.parish_code && atLeastOneOtherAttributeSet)) {
      return [false, "Parish Code and (Crown Allotment Number or Section or Portion or Block) must be set together"];
    }

    if (titleDetails.lot_number && !/^[A-Z\d]+$/.test(titleDetails.lot_number)) {
      return [false, "Lot Number not alphanumeric"];
    }

    if (titleDetails.plan_prefix && !/^[A-Z]{2}$/.test(titleDetails.plan_prefix)) {
      return [false, "Plan Prefix not 2 uppercase letters"];
    }

    if (titleDetails.plan_number && !/^[A-Z\d]+$/.test(titleDetails.plan_number)) {
      return [false, "Plan Number not alphanumeric"];
    }

    if (titleDetails.crown_allotment_number && !/^[A-Z\d]+$/.test(titleDetails.crown_allotment_number)) {
      return [false, "Crown Allotment Number not alphanumeric"];
    }

    if (titleDetails.section && !/^[A-Z\d]+$/.test(titleDetails.section)) {
      return [false, "Section not alphanumeric"];
    }

    if (titleDetails.block && !/^[A-Z\d]+$/.test(titleDetails.block)) {
      return [false, "Block not alphanumeric"];
    }

    if (titleDetails.portion && !/^[A-Z\d]+$/.test(titleDetails.portion)) {
      return [false, "Portion not alphanumeric"];
    }

    if (titleDetails.parish_code && !/^\d{4}$/.test(titleDetails.parish_code)) {
      return [false, "Parish Code not 3 digits"];
    }

    return [true, null];
  };

  const isTitleDetailsValid_WA = (titleDetails) => {
    if (titleDetails.lot_type && !/^[A-Z]+$/.test(titleDetails.lot_type)) {
      return [false, "Lot Type not uppercase letters"];
    }

    if (titleDetails.lot_number && !/^[A-Z\d]+$/.test(titleDetails.lot_number)) {
      return [false, "Lot Number not alphanumeric"];
    }

    if (!/^[A-Z]{1}$/.test(titleDetails.plan_prefix || "")) {
      return [false, "Plan Prefix not 1 uppercase letter"];
    }

    if (!/^\d+$/.test(titleDetails.plan_number || "")) {
      return [false, "Plan Number not numeric"];
    }

    return [true, null];
  };

  const onlyParcelDescriptionSet = (parcel) => {
    const otherFields = [
      "block",
      "section",
      "portion",
      "crown_allotment_number",
      "lot_type",
      "lot_number",
      "plan_prefix",
      "plan_number",
      "survey_plan",
      "unit",
      "district",
      "division",
      "location_code",
      "parish_code",
    ];

    const noOtherFieldsSet = otherFields.every((field) => !parcel?.[field]);
    return Boolean(parcel?.jurisdiction_id) && noOtherFieldsSet;
  };

  const runTitleDetailsValidation = useCallback((titleDetail, activeField) => {
    const validatorsForState = {
      ACT: isTitleDetailsValid_ACT,
      NSW: isTitleDetailsValid_NSW,
      NT: isTitleDetailsValid_NT,
      QLD: isTitleDetailsValid_QLD,
      SA: isTitleDetailsValid_SA,
      TAS: isTitleDetailsValid_TAS,
      VIC: isTitleDetailsValid_VIC,
      WA: isTitleDetailsValid_WA,
    };

    const normalizedStateCode = (stateCode || "").toString().trim().toUpperCase();
    const validator = validatorsForState[normalizedStateCode];
    if (!validator) {
      return { isValid: true, message: "" };
    }

    const [isValid, validationMessage] = validator(titleDetail);
    if (onlyParcelDescriptionSet(titleDetail)) {
      return { isValid: true, message: "" };
    }

    if (!isValid) {
      const parcelLabel = titleDetail?.parcel_id ? `Parcel #${titleDetail.parcel_id}` : "New parcel";
      return { isValid: false, message: `${parcelLabel} invalid and was NOT saved:\n${validationMessage}` };
    }

    if (activeField !== "jurisdiction_id") {
      titleDetail.jurisdiction_id = null;
    }

    return { isValid: true, message: "" };
  }, [stateCode]);

  const persistRows = useCallback(async (nextRows, rowIndex) => {
    const record = nextRows[rowIndex];
    if (!record || !record.modified) {
      return;
    }

    const response = await saveRecord?.(record, ENDPOINT, PRIMARY_KEY);
    if (response?.result === false) {
      window.alert(response.error_message || "Unable to save parcel record.");
      return;
    }

    const savedRows = [...nextRows];
    savedRows[rowIndex] = {
      ...savedRows[rowIndex],
      modified: false,
      new_record: false,
      [PRIMARY_KEY]: response?.id ?? savedRows[rowIndex][PRIMARY_KEY],
    };

    syncRows(savedRows);
    await dataFetch?.();
  }, [dataFetch, saveRecord, syncRows]);

  const updateCell = useCallback((rowIndex, field, value) => {
    const nextRows = [...rowsRef.current];
    const current = { ...(nextRows[rowIndex] || {}) };

    current[field] = value;
    current.modified = true;
    current.last_edited_by = user;

    if (field !== "jurisdiction_id") {
      current.jurisdiction_id = null;
    }

    nextRows[rowIndex] = current;
    rowsRef.current = nextRows;
    syncRows(nextRows);
  }, [syncRows, user]);

  const commitCell = useCallback(async (rowIndex, field, value) => {
    const nextRows = [...rowsRef.current];
    const current = { ...(nextRows[rowIndex] || {}) };

    current[field] = value;
    current.modified = true;
    current.last_edited_by = user;

    nextRows[rowIndex] = current;
    rowsRef.current = nextRows;
    // Keep the typed value in UI even when validation fails and save is blocked.
    syncRows(nextRows);

    const titleValidation = runTitleDetailsValidation(current, field);
    if (!titleValidation.isValid) {
      showValidationError(titleValidation.message);
      return;
    }

    const relatedValidation = runRelatedValidation(current);
    if (!relatedValidation.isValid) {
      showValidationError(relatedValidation.message);
      return;
    }

    clearValidationError();

    await persistRows(nextRows, rowIndex);
  }, [clearValidationError, persistRows, runRelatedValidation, runTitleDetailsValidation, showValidationError, syncRows, user]);

  const addRecord = useCallback(() => {
    clearValidationError();

    const nextRows = [
      {
        [PRIMARY_KEY]: null,
        [foreignKey.key]: foreignKey.value,
        jurisdiction_id: null,
        state_code: null,
        created_by: user,
        last_edited_by: null,
        new_record: true,
        modified: true,
      },
      ...rows,
    ];

    syncRows(nextRows);
  }, [clearValidationError, foreignKey.key, foreignKey.value, rows, syncRows, user]);

  const copyRecord = useCallback((rowIndex) => {
    const source = rowsRef.current[rowIndex];
    if (!source) {
      return;
    }

    const requestedCopies = Number.parseInt(window.prompt("How many copies do you want to create?", "1"), 10);
    if (!requestedCopies || requestedCopies < 1) {
      return;
    }

    const copies = Array.from({ length: requestedCopies }, (_, copyIndex) => {
      const parsedLotNumber = Number.parseInt(source.lot_number, 10);
      return {
        ...source,
        [PRIMARY_KEY]: null,
        lot_number: Number.isNaN(parsedLotNumber) ? source.lot_number : String(parsedLotNumber + copyIndex + 1),
        created_by: user,
        last_edited_by: user,
        new_record: true,
        modified: true,
      };
    });

    const nextRows = [...rowsRef.current];
    nextRows.splice(rowIndex + 1, 0, ...copies);
    syncRows(nextRows);
  }, [syncRows, user]);

  const deleteRow = useCallback(async (rowIndex) => {
    const row = rowsRef.current[rowIndex];
    if (!row) {
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this parcel?");
    if (!confirmed) {
      return;
    }

    if (row[PRIMARY_KEY]) {
      const response = await saveRecord?.(
        {
          ...row,
          archived_date: new Date().toISOString(),
          modified: true,
          last_edited_by: user,
        },
        ENDPOINT,
        PRIMARY_KEY
      );

      if (response?.result === false) {
        window.alert(response.error_message || "Unable to delete parcel record.");
        return;
      }
    }

    syncRows(rowsRef.current.filter((_, index) => index !== rowIndex));
    await dataFetch?.();
  }, [dataFetch, saveRecord, syncRows, user]);

  const deleteAllRows = useCallback(async () => {
    clearValidationError();

    if (!rowsRef.current.length) {
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete all Parcels?");
    if (!confirmed) {
      return;
    }

    const nextRows = [...rowsRef.current];
    for (let index = nextRows.length - 1; index >= 0; index -= 1) {
      const row = nextRows[index];

      if (!row?.[PRIMARY_KEY]) {
        nextRows.splice(index, 1);
        continue;
      }

      const response = await saveRecord?.(
        {
          ...row,
          archived_date: new Date().toISOString(),
          modified: true,
          last_edited_by: user,
        },
        ENDPOINT,
        PRIMARY_KEY
      );

      if (response?.result === false) {
        window.alert(response.error_message || "Unable to delete all parcel records.");
        return;
      }

      nextRows.splice(index, 1);
    }

    syncRows(nextRows);
    await dataFetch?.();
  }, [clearValidationError, dataFetch, saveRecord, syncRows, user]);

  useEffect(() => {
    tableHandlersRef.current = {
      updateCell,
      commitCell,
      deleteRow,
      copyRecord,
    };
  }, [commitCell, copyRecord, deleteRow, updateCell]);

  const editableCell = useCallback(
    (field, placeholder = "") =>
      (info) => (
        <Form.Control
          size="sm"
          value={info.getValue() ?? ""}
          placeholder={placeholder}
          onChange={(event) => tableHandlersRef.current.updateCell?.(info.row.index, field, event.target.value)}
          onBlur={(event) => tableHandlersRef.current.commitCell?.(info.row.index, field, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              tableHandlersRef.current.commitCell?.(info.row.index, field, event.currentTarget.value);
            }
          }}
        />
      ),
    []
  );

  const disabledCell = useCallback(
    (field, controlProps = {}) =>
      (info) => <Form.Control size="sm" value={info.row.original?.[field] ?? ""} readOnly disabled {...controlProps} />,
    []
  );

  // Keep column definitions stable across keystrokes to avoid remounting editable cells.
  const candidateColumns = useMemo(
    () => [
      columnHelper.accessor("parcel_id", { id: "parcel_id", header: "ID", size: 70 }),
      columnHelper.accessor("jurisdiction_id", { id: "jurisdiction_id", header: "Parcel Description", cell: disabledCell("jurisdiction_id"), size: 220, meta: { wrap: true } }),
      columnHelper.accessor("block", { id: "block", header: "Block", cell: editableCell("block"), size: 90, meta: { states: ["ACT", "VIC"] } }),
      columnHelper.accessor("section", { id: "section", header: "Section", cell: editableCell("section"), size: 90, meta: { states: ["ACT", "NSW", "VIC"] } }),
      // columnHelper.accessor("portion", { id: "portion", header: "Portion", cell: editableCell("portion"), size: 90, meta: { states: ["VIC"] } }),
      // columnHelper.accessor("crown_allotment_number", { id: "crown_allotment_number", header: "Crown Allotment", cell: editableCell("crown_allotment_number"), size: 140, meta: { states: ["VIC"] } }),
      // columnHelper.accessor("lot_type", { id: "lot_type", header: "Lot Type", cell: editableCell("lot_type"), size: 90, meta: { states: ["SA", "WA"] } }),
      // columnHelper.accessor("lot_number", { id: "lot_number", header: "Lot Number", cell: editableCell("lot_number"), size: 90, meta: { states: ["NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"] } }),
      // columnHelper.accessor("plan_prefix", { id: "plan_prefix", header: "Plan Prefix", cell: editableCell("plan_prefix"), size: 90, meta: { states: ["NSW", "QLD", "SA", "VIC", "WA"] } }),
      // columnHelper.accessor("plan_number", { id: "plan_number", header: "Plan Number", cell: editableCell("plan_number"), size: 100, meta: { states: ["NSW", "QLD", "SA", "TAS", "VIC", "WA"] } }),
      // columnHelper.accessor("survey_plan", { id: "survey_plan", header: "Survey Plan", cell: editableCell("survey_plan"), size: 100, meta: { states: ["NT"] } }),
      columnHelper.accessor("unit", { id: "unit", header: "Unit", cell: editableCell("unit"), size: 80, meta: { states: ["ACT", "NT"] } }),
      columnHelper.accessor("district", { id: "district", header: "District", cell: editableCell("district"), size: 100, meta: { states: ["ACT"] } }),
      columnHelper.accessor("division", { id: "division", header: "Division", cell: editableCell("division"), size: 100, meta: { states: ["ACT"] } }),
      // columnHelper.accessor("location_code", { id: "location_code", header: "Location Code", cell: editableCell("location_code"), size: 110, meta: { states: ["NT"] } }),
      // columnHelper.accessor("parish_code", { id: "parish_code", header: "Parish Code", cell: editableCell("parish_code"), size: 100, meta: { states: ["VIC"] } }),
      columnHelper.accessor("state_code", { id: "state_code", header: "State Code", size: 70 }),
      columnHelper.accessor("geometry", { id: "geometry", header: "Geometry", cell: disabledCell("geometry", { as: "textarea", rows: 2 }), size: 160, meta: { wrap: true } }),
      columnHelper.accessor("created_date", { id: "created_date", header: "Created Date", cell: (info) => formatDate(info.getValue()), size: 100 }),
      columnHelper.accessor("created_by", { id: "created_by", header: "Created By", size: 100 }),
      columnHelper.accessor("last_edited_date", { id: "last_edited_date", header: "Last Edited", cell: (info) => formatDate(info.getValue()), size: 100 }),
      columnHelper.accessor("last_edited_by", { id: "last_edited_by", header: "Last Edited By", size: 100 }),
      columnHelper.display({
        id: "delete",
        header: "",
        size: 80,
        cell: (info) => (
          <div className="d-flex justify-content-center w-100">
            <Button type="button" size="sm" variant="danger" onClick={() => tableHandlersRef.current.deleteRow?.(info.row.index)}>
              Delete
            </Button>
          </div>
        ),
      }),
      columnHelper.display({
        id: "copy",
        header: "",
        size: 80,
        cell: (info) => (
          <div className="d-flex justify-content-center w-100">
            <Button type="button" size="sm" variant="secondary" onClick={() => tableHandlersRef.current.copyRecord?.(info.row.index)}>
              Copy
            </Button>
          </div>
        ),
      }),
    ],
    [columnHelper, disabledCell, editableCell]
  );

  const columns = useMemo(
    () => candidateColumns.filter((column) => !column.columnDef?.meta?.states || !stateCode || column.columnDef.meta.states.includes(stateCode)),
    [candidateColumns, stateCode]
  );

  const renderList = () => {
    const limit = Number.parseInt(listAmount, 10) || 5;
    return rows.slice(0, limit).map((row, index) => (
      <ListGroup.Item key={row.parcel_id ?? `parcel-${index}`}>
        {row.jurisdiction_id || "Parcel details incomplete"}
      </ListGroup.Item>
    ));
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="TitleDetailsSection" id={elementIds.container}>
      {showAlert && (
        <Row className="show-Container g-2 mb-2">
          <Col xs={12}>
            <Alert variant="danger" dismissible onClose={() => setShowAlert(false)}>
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {controlType === "table" && (
        <Row className="show-Container g-2 mb-2">
          <Col xs={12} className="text-right">
            <div className="d-inline-flex align-items-center gap-2">
              <Button id={elementIds.addButton} variant="primary" size="sm" onClick={addRecord}>
                Add
              </Button>
              <Button id={elementIds.deleteAllButton} variant="danger" size="sm" onClick={deleteAllRows}>
                Delete All
              </Button>
            </div>
          </Col>
        </Row>
      )}

      <Row className="show-Container g-2">
        <Col xs={12}>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader cssOverride={{ display: "block", margin: "0 auto" }} size={20} color="#e03c31" loading={isLoading} />
              </div>
            </div>
          ) : controlType === "table" ? (
            <GenericTable
              columns={columns}
              data={rows}
              pageSize={PAGE_SIZES.slmTitleDetailsList}
              enablePagination={true}
              hasHeaderSorting={true}
              section="slm-title-details-list"
              elementIds={elementIds.table}
              containerClassName="componentRendering"
              enforceMinRowsHeight={true}
            />
          ) : (
            <ListGroup>{renderList()}</ListGroup>
          )}
        </Col>
      </Row>
    </div>
  );
}
